/**
 * Multi-Step Worker-Onboarding-Form.
 *
 * 3 Steps: Persönlich → Steuer/Bank → Sozialversicherung/Aufenthalt.
 * Auto-Save passiert beim Klick auf "Weiter" (nicht beim Tippen — würde
 * zu viel Traffic erzeugen). Final Submit prüft Pflichtfelder serverseitig.
 *
 * Pflichtfelder-Marker (*) sind kosmetisch; die echte Validation
 * passiert beim `submit` durch das Server-Schema.
 */
import {
  upsertWorkerSchema,
  type UpsertWorkerInput,
  INSURANCE_STATUSES,
  MARITAL_STATUSES,
} from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Field } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';

type FormValues = UpsertWorkerInput;

const STEPS = [
  { id: 1, title: 'Persönlich', description: 'Stammdaten und Anschrift' },
  { id: 2, title: 'Steuer & Bank', description: 'Lohnabrechnung und Auszahlung' },
  {
    id: 3,
    title: 'Sozialversicherung & Aufenthalt',
    description: 'Krankenkasse, ggf. Arbeitserlaubnis',
  },
] as const;

const stripEmpty = (input: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === '' || v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
};

export function WorkerOnboardingForm() {
  const utils = trpc.useUtils();
  const profile = trpc.workers.getMine.useQuery();
  const upsert = trpc.workers.upsertMine.useMutation({
    onSuccess: () => utils.workers.getMine.invalidate(),
  });
  const submit = trpc.workers.submit.useMutation({
    onSuccess: () => {
      toast.success('Profil eingereicht. Internal Staff prüft jetzt.');
      utils.workers.getMine.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const [step, setStep] = useState(1);

  const form = useForm<FormValues>({
    resolver: zodResolver(upsertWorkerSchema),
    defaultValues: {},
    mode: 'onBlur',
  });

  // Initial-Load: Form aus Server-Daten füllen.
  useEffect(() => {
    if (!profile.data) return;
    const w = profile.data;
    form.reset({
      firstName: w.firstName ?? undefined,
      lastName: w.lastName ?? undefined,
      maidenName: w.maidenName ?? undefined,
      dateOfBirth: w.dateOfBirth ?? undefined,
      placeOfBirth: w.placeOfBirth ?? undefined,
      addressStreet: w.addressStreet ?? undefined,
      addressZip: w.addressZip ?? undefined,
      addressCity: w.addressCity ?? undefined,
      addressCountry: w.addressCountry ?? undefined,
      phone: w.phone ?? undefined,
      nationality: w.nationality ?? undefined,
      taxId: w.taxId ?? undefined,
      taxClass: w.taxClass ?? undefined,
      religiousAffiliation: w.religiousAffiliation ?? undefined,
      childAllowances: w.childAllowances ?? undefined,
      maritalStatus: w.maritalStatus ?? undefined,
      numberOfChildren: w.numberOfChildren ?? undefined,
      bankAccountHolder: w.bankAccountHolder ?? undefined,
      iban: w.iban ?? undefined,
      bic: w.bic ?? undefined,
      socialSecurityNumber: w.socialSecurityNumber ?? undefined,
      healthInsuranceProvider: w.healthInsuranceProvider ?? undefined,
      insuranceStatus: w.insuranceStatus ?? undefined,
      residencePermitType: w.residencePermitType ?? undefined,
      residencePermitExpiry: w.residencePermitExpiry ?? undefined,
      workAuthorizationConfirmed: w.workAuthorizationConfirmed ?? undefined,
      workRestrictions: w.workRestrictions ?? undefined,
    });
  }, [profile.data, form]);

  const readOnly =
    profile.data?.status === 'approved' ||
    profile.data?.status === 'pending_review' ||
    profile.data?.status === 'blocked';

  const handleNext = async () => {
    const values = form.getValues();
    try {
      await upsert.mutateAsync(stripEmpty(values) as FormValues);
      toast.success('Zwischenstand gespeichert.');
      if (step < STEPS.length) setStep(step + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler beim Speichern');
    }
  };

  const handleSubmit = async () => {
    const values = form.getValues();
    try {
      await upsert.mutateAsync(stripEmpty(values) as FormValues);
      await submit.mutateAsync();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler beim Submit');
    }
  };

  if (profile.isLoading) {
    return <div className="text-center text-[var(--muted-foreground)] py-12">Lade Profil …</div>;
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-[var(--muted-foreground)]">
          Schritt {step} von {STEPS.length}
        </p>
        <h1 className="text-2xl font-bold">{STEPS[step - 1].title}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{STEPS[step - 1].description}</p>
      </header>

      {readOnly && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-[var(--muted-foreground)]">
              Status: <span className="font-mono">{profile.data?.status}</span>. Bearbeitung nur in
              den Status <span className="font-mono">draft</span> oder{' '}
              <span className="font-mono">rejected</span> möglich.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{STEPS[step - 1].title}</CardTitle>
          <CardDescription>{STEPS[step - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4">
            {step === 1 && <Step1 form={form} disabled={readOnly} />}
            {step === 2 && <Step2 form={form} disabled={readOnly} />}
            {step === 3 && <Step3 form={form} disabled={readOnly} />}
          </form>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
        >
          Zurück
        </Button>
        {step < STEPS.length ? (
          <Button onClick={handleNext} disabled={readOnly || upsert.isPending}>
            {upsert.isPending ? 'Speichere …' : 'Weiter'}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={readOnly || submit.isPending || upsert.isPending}
          >
            {submit.isPending ? 'Reiche ein …' : 'Profil einreichen'}
          </Button>
        )}
      </div>
    </div>
  );
}

type StepProps = {
  form: ReturnType<typeof useForm<FormValues>>;
  disabled?: boolean;
  allowUnsetBoolean?: boolean;
};

function Step1({ form, disabled }: StepProps) {
  const { register, formState } = form;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Vorname *" htmlFor="firstName" error={formState.errors.firstName?.message}>
        <Input id="firstName" disabled={disabled} {...register('firstName')} />
      </Field>
      <Field label="Nachname *" htmlFor="lastName" error={formState.errors.lastName?.message}>
        <Input id="lastName" disabled={disabled} {...register('lastName')} />
      </Field>
      <Field label="Geburtsname" htmlFor="maidenName">
        <Input id="maidenName" disabled={disabled} {...register('maidenName')} />
      </Field>
      <Field
        label="Geburtsdatum *"
        htmlFor="dateOfBirth"
        error={formState.errors.dateOfBirth?.message}
      >
        <Input id="dateOfBirth" type="date" disabled={disabled} {...register('dateOfBirth')} />
      </Field>
      <Field label="Geburtsort" htmlFor="placeOfBirth">
        <Input id="placeOfBirth" disabled={disabled} {...register('placeOfBirth')} />
      </Field>
      <Field label="Staatsangehörigkeit *" htmlFor="nationality">
        <Input id="nationality" disabled={disabled} {...register('nationality')} />
      </Field>
      <Field
        label="Straße & Hausnummer *"
        htmlFor="addressStreet"
        error={formState.errors.addressStreet?.message}
      >
        <Input id="addressStreet" disabled={disabled} {...register('addressStreet')} />
      </Field>
      <Field label="PLZ *" htmlFor="addressZip">
        <Input id="addressZip" disabled={disabled} {...register('addressZip')} />
      </Field>
      <Field label="Stadt *" htmlFor="addressCity">
        <Input id="addressCity" disabled={disabled} {...register('addressCity')} />
      </Field>
      <Field label="Land (ISO-2)" htmlFor="addressCountry" hint="z.B. DE">
        <Input
          id="addressCountry"
          disabled={disabled}
          placeholder="DE"
          {...register('addressCountry')}
        />
      </Field>
      <Field label="Telefon *" htmlFor="phone">
        <Input id="phone" disabled={disabled} {...register('phone')} />
      </Field>
    </div>
  );
}

function Step2({ form, disabled }: StepProps) {
  const { register, formState, watch, setValue } = form;
  const maritalStatus = watch('maritalStatus');
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field
        label="Steueridentifikationsnummer *"
        htmlFor="taxId"
        error={formState.errors.taxId?.message}
        hint="11-stellig"
      >
        <Input id="taxId" disabled={disabled} {...register('taxId')} />
      </Field>
      <Field label="Steuerklasse" htmlFor="taxClass">
        <Input id="taxClass" disabled={disabled} {...register('taxClass')} />
      </Field>
      <Field label="Konfession" htmlFor="religiousAffiliation" hint="rk / ev / keine">
        <Input
          id="religiousAffiliation"
          disabled={disabled}
          {...register('religiousAffiliation')}
        />
      </Field>
      <Field label="Familienstand" htmlFor="maritalStatus">
        <Select
          disabled={disabled}
          value={maritalStatus ?? undefined}
          onValueChange={(v) => setValue('maritalStatus', v as FormValues['maritalStatus'])}
        >
          <SelectTrigger id="maritalStatus">
            <SelectValue placeholder="Bitte wählen" />
          </SelectTrigger>
          <SelectContent>
            {MARITAL_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s === 'single'
                  ? 'ledig'
                  : s === 'married'
                    ? 'verheiratet'
                    : s === 'divorced'
                      ? 'geschieden'
                      : s === 'widowed'
                        ? 'verwitwet'
                        : 'eingetragene Partnerschaft'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Kinder" htmlFor="numberOfChildren">
        <Input
          id="numberOfChildren"
          type="number"
          min={0}
          disabled={disabled}
          {...register('numberOfChildren', { valueAsNumber: true })}
        />
      </Field>
      <Field label="Kinderfreibeträge" htmlFor="childAllowances">
        <Input
          id="childAllowances"
          type="number"
          min={0}
          step={0.5}
          disabled={disabled}
          {...register('childAllowances', { valueAsNumber: true })}
        />
      </Field>
      <Field label="Kontoinhaber" htmlFor="bankAccountHolder">
        <Input id="bankAccountHolder" disabled={disabled} {...register('bankAccountHolder')} />
      </Field>
      <Field label="IBAN *" htmlFor="iban" error={formState.errors.iban?.message} hint="DE…">
        <Input id="iban" disabled={disabled} {...register('iban')} />
      </Field>
      <Field label="BIC" htmlFor="bic">
        <Input id="bic" disabled={disabled} {...register('bic')} />
      </Field>
    </div>
  );
}

function Step3({ form, disabled, allowUnsetBoolean }: StepProps) {
  const { register, formState, watch, setValue } = form;
  const insuranceStatus = watch('insuranceStatus');
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Sozialversicherungsnummer *" htmlFor="socialSecurityNumber">
        <Input
          id="socialSecurityNumber"
          disabled={disabled}
          {...register('socialSecurityNumber')}
        />
      </Field>
      <Field label="Krankenkasse *" htmlFor="healthInsuranceProvider" hint="z.B. AOK Bayern">
        <Input
          id="healthInsuranceProvider"
          disabled={disabled}
          {...register('healthInsuranceProvider')}
        />
      </Field>
      <Field label="Versicherungsstatus *" htmlFor="insuranceStatus">
        <Select
          disabled={disabled}
          value={insuranceStatus ?? undefined}
          onValueChange={(v) => setValue('insuranceStatus', v as FormValues['insuranceStatus'])}
        >
          <SelectTrigger id="insuranceStatus">
            <SelectValue placeholder="Bitte wählen" />
          </SelectTrigger>
          <SelectContent>
            {INSURANCE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s === 'gkv' ? 'gesetzlich (GKV)' : 'privat (PKV)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field
        label="Aufenthaltstitel-Typ"
        htmlFor="residencePermitType"
        hint="eAT / Aufenthaltserlaubnis / …"
      >
        <Input id="residencePermitType" disabled={disabled} {...register('residencePermitType')} />
      </Field>
      <Field
        label="Aufenthaltstitel-Ablauf"
        htmlFor="residencePermitExpiry"
        error={formState.errors.residencePermitExpiry?.message}
      >
        <Input
          id="residencePermitExpiry"
          type="date"
          disabled={disabled}
          {...register('residencePermitExpiry')}
        />
      </Field>
      <Field label="Erwerbstätigkeit gestattet" htmlFor="workAuthorizationConfirmed">
        {allowUnsetBoolean ? (
          <Select
            disabled={disabled}
            value={
              watch('workAuthorizationConfirmed') == null
                ? 'unset'
                : watch('workAuthorizationConfirmed')
                  ? 'yes'
                  : 'no'
            }
            onValueChange={(value) =>
              setValue(
                'workAuthorizationConfirmed',
                value === 'unset' ? undefined : value === 'yes',
              )
            }
          >
            <SelectTrigger id="workAuthorizationConfirmed">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">Nicht hinterlegt</SelectItem>
              <SelectItem value="yes">Ja</SelectItem>
              <SelectItem value="no">Nein</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="flex items-center gap-2 pt-2">
            <input
              id="workAuthorizationConfirmed"
              type="checkbox"
              disabled={disabled}
              {...register('workAuthorizationConfirmed')}
              className="size-4"
            />
            <span className="text-sm text-[var(--muted-foreground)]">
              Im Aufenthaltstitel ist „Erwerbstätigkeit gestattet" vermerkt
            </span>
          </div>
        )}
      </Field>
      <div className="md:col-span-2">
        <Field label="Beschränkungen / Hinweise" htmlFor="workRestrictions">
          <Input
            id="workRestrictions"
            disabled={disabled}
            placeholder="z. B. spezifische Tätigkeit oder Region"
            {...register('workRestrictions')}
          />
        </Field>
      </div>
    </div>
  );
}

// ─── Wiederverwendbare Feld-Steps ─────────────────────────────────────────────
// Identische Felder wie die Onboarding-Form, vom Staff-Edit (im Namen verwalten)
// genutzt. Erwarten ein `useForm<UpsertWorkerInput>` über `form`.
export { Step1 as WorkerProfileStep1, Step2 as WorkerProfileStep2, Step3 as WorkerProfileStep3 };
